import React, { useState } from 'react';
import {
    Box,
    CssBaseline,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Badge,
    Menu,
    MenuItem,
    Avatar,
    ListSubheader,
    Tooltip,
    useMediaQuery,
} from '@mui/material';
import {
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    Dashboard as DashboardIcon,
    Inventory as InventoryIcon,
    Backpack as PackIcon,
    Layers as LayersIcon,
    Store as StoreIcon,
    ExitToApp as LogoutIcon,
    Notifications as NotificationsIcon,
    Report as ReportIcon,
    AccountCircle as AccountCircleIcon,
    Payment as PaymentIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import AnnouncementBar from './components/AnnouncementBar';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        flexGrow: 1,
        padding: theme.spacing(3),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: 0,
        [theme.breakpoints.up('md')]: {
            marginLeft: open ? 0 : `-${drawerWidth}px`,
        },
    }),
);

const AppBarStyled = styled(AppBar, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        [theme.breakpoints.up('md')]: {
            ...(open && {
                width: `calc(100% - ${drawerWidth}px)`,
                marginLeft: `${drawerWidth}px`,
                transition: theme.transitions.create(['margin', 'width'], {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            }),
        },
    }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'space-between',
}));

const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
};

const Layout = ({ children, handleLogout, notifications, noteCount, announcement, onDismissAnnouncement }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(!isMobile);
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleNotificationOpen = (event) => setNotificationAnchorEl(event.currentTarget);
    const handleNotificationClose = () => setNotificationAnchorEl(null);

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
        { text: 'Products', icon: <InventoryIcon />, path: '/products' },
        { text: 'Bundles', icon: <LayersIcon />, path: '/bundles' },
        { text: 'Variations & Wholesale', icon: <PackIcon />, path: '/packbundles' },
        { text: 'Bundle Options', icon: <StoreIcon />, path: '/storeoptions' },
        { text: 'Reports', icon: <ReportIcon />, path: '/reports' },
    ];

    const drawerContent = (
        <>
            <DrawerHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
                    <img
                        src="https://app.bundlefix.com/logo-bundlefix.png"
                        alt="BundleFix Logo"
                        style={{ height: 36, objectFit: 'contain' }}
                    />
                </Box>
                <IconButton onClick={handleDrawerClose} sx={{ color: '#94A3B8' }}>
                    <ChevronLeftIcon />
                </IconButton>
            </DrawerHeader>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
            <List sx={{ px: 1, pt: 1 }}>
                {menuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                                selected={isActive}
                                onClick={() => {
                                    navigate(item.path);
                                    if (isMobile) setOpen(false);
                                }}
                                sx={{
                                    borderRadius: 2,
                                    color: isActive ? '#FFFFFF' : '#94A3B8',
                                    backgroundColor: isActive ? 'rgba(37,99,235,0.85)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: isActive ? 'rgba(37,99,235,0.9)' : 'rgba(255,255,255,0.06)',
                                        color: '#FFFFFF',
                                        '& .MuiListItemIcon-root': { color: '#FFFFFF' },
                                    },
                                    '& .MuiListItemIcon-root': {
                                        color: isActive ? '#FFFFFF' : '#94A3B8',
                                        minWidth: 40,
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(37,99,235,0.85)',
                                        '&:hover': { backgroundColor: 'rgba(37,99,235,0.9)' },
                                    },
                                }}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText
                                    primary={item.text}
                                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 400 }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBarStyled position="fixed" open={open && !isMobile}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{ mr: 2, ...(open && !isMobile && { display: 'none' }) }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {(!open || isMobile) && (
                        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                            <img
                                src="https://app.bundlefix.com/logo-bundlefix.png"
                                alt="BundleFix Logo"
                                style={{ height: 30, objectFit: 'contain' }}
                            />
                        </Box>
                    )}

                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600, color: '#1E293B' }}>
                        Ecwid Bundle Admin
                    </Typography>

                    <Tooltip title="Notifications">
                        <IconButton color="inherit" onClick={handleNotificationOpen} sx={{ color: '#64748B' }}>
                            <Badge badgeContent={noteCount} color="error">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    <Menu
                        anchorEl={notificationAnchorEl}
                        open={Boolean(notificationAnchorEl)}
                        onClose={handleNotificationClose}
                        PaperProps={{ sx: { width: 320, borderRadius: 2, mt: 1 } }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E2E8F0' }}>
                            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                Notifications
                            </Typography>
                        </Box>
                        {notifications.length === 0 ? (
                            <MenuItem disabled>
                                <Typography variant="body2" color="text.secondary">No notifications</Typography>
                            </MenuItem>
                        ) : (
                            notifications.slice(0, 5).map((notif, index) => (
                                <MenuItem
                                    key={index}
                                    onClick={handleNotificationClose}
                                    sx={{
                                        py: 1.5,
                                        backgroundColor: notif.read_flag ? 'transparent' : 'rgba(37,99,235,0.05)',
                                        '&:hover': {
                                            backgroundColor: notif.read_flag ? 'action.hover' : 'rgba(37,99,235,0.08)',
                                        },
                                        borderLeft: notif.read_flag ? 'none' : '3px solid #2563EB',
                                    }}
                                >
                                    <Box display="flex" flexDirection="column" width="100%">
                                        <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.4 }}>
                                            {notif.details}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {formatDate(notif.createdAt)}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                        <Divider />
                        <MenuItem
                            onClick={() => { navigate('/notifications'); handleNotificationClose(); }}
                            sx={{ justifyContent: 'center', color: 'primary.main', fontWeight: 600, fontSize: '0.875rem' }}
                        >
                            View all notifications
                        </MenuItem>
                    </Menu>

                    <Tooltip title="Account">
                        <IconButton color="inherit" onClick={handleMenuOpen} sx={{ ml: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                                <AccountCircleIcon sx={{ fontSize: 20 }} />
                            </Avatar>
                        </IconButton>
                    </Tooltip>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 160 } }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
                            <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                            Profile
                        </MenuItem>
                        <MenuItem onClick={() => { navigate('/billing'); handleMenuClose(); }}>
                            <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
                            Billing
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                            <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBarStyled>

            {/* Mobile: temporary overlay drawer */}
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    open={open}
                    onClose={handleDrawerClose}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
                    }}
                >
                    {drawerContent}
                </Drawer>
            ) : (
                <Drawer
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
                    }}
                    variant="persistent"
                    anchor="left"
                    open={open}
                >
                    {drawerContent}
                </Drawer>
            )}

            <Main open={open}>
                <DrawerHeader />
                <Box sx={{ mx: -3, mt: -3, mb: announcement ? 1 : 0 }}>
                    <AnnouncementBar announcement={announcement} onDismiss={onDismissAnnouncement} />
                </Box>
                {children}
            </Main>
        </Box>
    );
};

export default Layout;
